import { isFirefox } from 'shared/lib/deviceDetector'

export function getCaretIndex(element: HTMLDivElement): {
  offset: number
  focusNode: Node | null | undefined
} {
  const isSupported = typeof window.getSelection !== 'undefined'

  if (!isSupported) {
    return {
      offset: 0,
      focusNode: null,
    }
  }

  const selection = window.getSelection()

  if (!selection || selection.rangeCount === 0) {
    return {
      offset: 0,
      focusNode: null,
    }
  }

  const range = selection.getRangeAt(0)

  if (!range) {
    return {
      offset: 0,
      focusNode: null,
    }
  }

  const preCaretRange = range.cloneRange()

  let absoluteOffset = selection.focusOffset || 0
  let focusNode = selection.focusNode

  try {
    preCaretRange.selectNodeContents(element)
    preCaretRange.setEnd(range.endContainer, range.endOffset)
  } catch (error) {
    console.error('getCaretIndex preCaretRange error:', error)

    return {
      offset: absoluteOffset || 0,
      focusNode: focusNode,
    }
  }

  if (isFirefox) {
    const originalFocusNode = focusNode

    focusNode =
      focusNode === preCaretRange.endContainer &&
      focusNode !== preCaretRange.startContainer.childNodes?.[0]
        ? preCaretRange.startContainer.childNodes?.[0]
        : focusNode

    if (
      focusNode === element ||
      (focusNode && focusNode.nodeType === Node.ELEMENT_NODE && focusNode !== range.endContainer)
    ) {
      // Try to find the actual text node at the caret position using the range's endContainer
      const actualNode = range.endContainer

      if (actualNode && actualNode.nodeType === Node.TEXT_NODE) {
        focusNode = actualNode
        absoluteOffset = range.endOffset
      } else if (actualNode && actualNode.childNodes) {
        // If endContainer is an element, try to find the text node at the endOffset
        const childAtOffset =
          actualNode.childNodes[range.endOffset - 1] ||
          actualNode.childNodes[range.endOffset] ||
          actualNode.firstChild

        if (childAtOffset && childAtOffset.nodeType === Node.TEXT_NODE) {
          focusNode = childAtOffset
          // Calculate offset within the child text node
          absoluteOffset =
            range.endOffset > 0 && actualNode.childNodes[range.endOffset - 1] === childAtOffset
              ? childAtOffset.textContent?.length || 0
              : 0
        }
      }
    } else if (focusNode !== originalFocusNode) {
      // If we changed the focusNode in the first correction, recalculate offset
      if (focusNode && focusNode.nodeType === Node.TEXT_NODE) {
        // Try to maintain relative position within the text node
        absoluteOffset = Math.min(absoluteOffset, focusNode.textContent?.length || 0)
      }
    }
  }

  return {
    offset: absoluteOffset,
    focusNode,
  }
}
